import { useRef, useState, type FormEvent } from "react";
import { useContextoApp } from "@/app/contexto";
import { useApiResource } from "@/hooks/useApiResource";
import { useAsyncAction } from "@/hooks/useAsyncAction";
import {
  api,
  type Adjunto,
  type Caja,
  type DetalleDireccion,
  type Fideicomiso,
  type FiduciantePublico,
  type Grupo,
  type ItemCompra,
  type ItemPrevisto,
  type SugerenciaDireccion,
  type Terreno,
  type TipoCambioSugerido,
} from "@/lib/api";
import { validarArchivo } from "@/lib/adjuntos";
import { cajaPorDefecto, hayVariasCajas, opcionesCaja } from "@/lib/cajas";
import { feePrevisto } from "@/lib/gastos";
import {
  adjuntoEscrituraSugerido,
  aplicarDetalleDireccion,
  cambiarCampoTerreno,
  nuevoTokenSesion,
  validarConfirmar,
  type CampoDireccion,
  type ErroresConfirmar,
  type FormConfirmar,
  PAGO_FIDEICOMISO,
  opcionesQuienPuso,
  restoDelFideicomiso,
  type LineaPuso,
  type ParteCaja,
  aPayloadItem,
  aPayloadPrevisto,
  aPayloadPrecio,
  aPayloadTerreno,
  formItem,
  formPrevisto,
  formPrecio,
  formTerreno,
  validarItem,
  validarPrecio,
  validarTerreno,
  type ErroresItem,
  type ErroresPrecio,
  type ErroresTerreno,
  type FormItem,
  type FormPrecio,
  type FormTerreno,
  type PayloadItem,
  type PayloadPrecio,
  type PayloadPrevisto,
  type PayloadTerreno,
  type TipoItemCompra,
} from "@/lib/terreno";

const SIN_ERRORES_TERRENO: ErroresTerreno = {};
const SIN_ERRORES_PRECIO: ErroresPrecio = {};
const SIN_ERRORES_ITEM: ErroresItem = {};
const SIN_ERRORES_CONFIRMAR: ErroresConfirmar = {};

/**
 * Lógica de Obra → Terreno (sin JSX). Los cuatro roles leen; el admin edita la ubicación, carga la compra (precio
 * acordado en USD y fecha de escritura opcional), agrega pagos a cuenta (un gasto pagado del rubro "Terreno"; si lo
 * pagó un fiduciante, también su aporte) y gastos de la compra (previstos hasta confirmar la compra; después, pagados),
 * con su archivo opcional, y sube la escritura. Al escribano la API no le manda la plata
 * (`compra` en null) y la pantalla no muestra ese bloque.
 */
export function useTerrenoTab() {
  const { fid, membresia } = useContextoApp();
  const ruta = `/api/fideicomisos/${fid}/terreno`;
  const esAdmin = membresia.rol === "admin";
  const ficha = useApiResource<Terreno>(ruta);
  const fideicomiso = useApiResource<Fideicomiso>(esAdmin ? `/api/fideicomisos/${fid}` : null);
  const tcSugerido = useApiResource<TipoCambioSugerido>(esAdmin ? `/api/fideicomisos/${fid}/tipo-cambio-sugerido` : null);
  const fiduciantes = useApiResource<FiduciantePublico[]>(esAdmin ? `/api/fideicomisos/${fid}/fiduciantes` : null);
  const cajas = useApiResource<Caja[]>(esAdmin ? `/api/fideicomisos/${fid}/cajas` : null);
  const grupos = useApiResource<Grupo[]>(esAdmin ? `/api/fideicomisos/${fid}/grupos` : null);

  // --- ubicación: tarjeta + diálogo con buscador de Google (un token de sesión por apertura) y campos a mano
  const [form, setForm] = useState<FormTerreno | null>(null);
  const [ubicacionAbierta, setUbicacionAbierta] = useState(false);
  const [intentado, setIntentado] = useState(false);
  const [buscadorFallo, setBuscadorFallo] = useState(false);
  const sesion = useRef("");
  const accionGuardar = useAsyncAction((cuerpo: PayloadTerreno) => api.patch<Terreno>(ruta, cuerpo));
  const accionDetalle = useAsyncAction((placeId: string) =>
    api.get<DetalleDireccion>(`/api/fideicomisos/${fid}/direcciones/${encodeURIComponent(placeId)}?sesion=${encodeURIComponent(sesion.current)}`),
  );

  const erroresTerreno = form ? validarTerreno(form) : SIN_ERRORES_TERRENO;

  const abrirUbicacion = () => {
    if (!ficha.data) return;
    setForm(formTerreno(ficha.data));
    setIntentado(false);
    setBuscadorFallo(false);
    accionGuardar.limpiarError();
    sesion.current = nuevoTokenSesion();
    setUbicacionAbierta(true);
  };

  const cambiar = (campo: CampoDireccion | "superficie_m2" | "zonificacion", valor: string) => setForm((f) => (f ? cambiarCampoTerreno(f, campo, valor) : f));

  const buscarDirecciones = (q: string) =>
    api.get<SugerenciaDireccion[]>(`/api/fideicomisos/${fid}/direcciones/sugerencias?q=${encodeURIComponent(q)}&sesion=${encodeURIComponent(sesion.current)}`);

  const elegirDireccion = async (sugerencia: SugerenciaDireccion) => {
    const detalle = await accionDetalle.run(sugerencia.place_id);
    if (!detalle) {
      setBuscadorFallo(true);
      return;
    }
    setForm((f) => (f ? aplicarDetalleDireccion(f, detalle) : f));
    sesion.current = nuevoTokenSesion(); // pedir el detalle cierra la sesión de Google
  };

  const guardar = async (e: FormEvent) => {
    e.preventDefault();
    setIntentado(true);
    if (!form || Object.keys(erroresTerreno).length > 0) return;
    if (await accionGuardar.run(aPayloadTerreno(form))) {
      setUbicacionAbierta(false);
      ficha.recargar();
    }
  };

  // --- compra: precio acordado y fecha de escritura
  const [precioAbierto, setPrecioAbierto] = useState(false);
  const [formPrecioActual, setFormPrecio] = useState<FormPrecio>({ precio: "", fecha_escritura: "" });
  const [mostrarErroresPrecio, setMostrarErroresPrecio] = useState(false);
  const accionPrecio = useAsyncAction((cuerpo: PayloadPrecio) => api.patch<Terreno>(ruta, cuerpo));
  const erroresPrecio = validarPrecio(formPrecioActual);

  const abrirPrecio = () => {
    if (!ficha.data) return;
    setFormPrecio(formPrecio(ficha.data));
    setMostrarErroresPrecio(false);
    accionPrecio.limpiarError();
    setPrecioAbierto(true);
  };

  const guardarPrecio = async (e: FormEvent) => {
    e.preventDefault();
    setMostrarErroresPrecio(true);
    if (Object.keys(erroresPrecio).length > 0) return;
    if (await accionPrecio.run(aPayloadPrecio(formPrecioActual))) {
      setPrecioAbierto(false);
      ficha.recargar();
    }
  };

  // --- compra: pagos a cuenta, gastos y previstos (el archivo se sube después del alta, como adjunto del gasto o del previsto)
  const [tipoItem, setTipoItem] = useState<TipoItemCompra | null>(null);
  const [editandoPrevisto, setEditandoPrevisto] = useState<ItemPrevisto | null>(null);
  const [formItemActual, setFormItem] = useState<FormItem>(() => formItem("pago"));
  const [mostrarErroresItem, setMostrarErroresItem] = useState(false);
  const [avisoArchivo, setAvisoArchivo] = useState<string | null>(null);
  const accionItem = useAsyncAction((cuerpo: PayloadItem) => api.post<ItemCompra>(`${ruta}/${tipoItem === "pago" ? "pagos" : "gastos"}`, cuerpo));
  const accionPrevisto = useAsyncAction(({ id, cuerpo }: { id: number | null; cuerpo: PayloadPrevisto }) =>
    id === null ? api.post<ItemPrevisto>(`${ruta}/previstos`, cuerpo) : api.patch<ItemPrevisto>(`${ruta}/previstos/${id}`, cuerpo),
  );
  const accionArchivo = useAsyncAction(({ url, archivo }: { url: string; archivo: File }) => {
    const form = new FormData();
    form.append("archivo", archivo);
    return api.upload<Adjunto>(url, form);
  });
  const feePct = fideicomiso.data?.fee_porcentaje ?? null;
  const erroresItem = validarItem(formItemActual, tipoItem ?? "pago");

  const abrirItem = (tipo: TipoItemCompra) => {
    setFormItem(formItem(tipo, undefined, tcSugerido.data?.tipo_cambio, cajaPorDefecto(cajas.data ?? [])));
    setEditandoPrevisto(null);
    setMostrarErroresItem(false);
    accionItem.limpiarError();
    accionPrevisto.limpiarError();
    setTipoItem(tipo);
  };

  const editarPrevisto = (p: ItemPrevisto) => {
    setFormItem(formPrevisto(p));
    setEditandoPrevisto(p);
    setMostrarErroresItem(false);
    accionPrevisto.limpiarError();
    setTipoItem("previsto");
  };

  const cambiarItem = <K extends keyof FormItem>(campo: K, valor: FormItem[K]) => setFormItem((f) => ({ ...f, [campo]: valor }));

  // Quién puso el pago a cuenta (Mati, 2026-10-19): uno ("Lo pagó"), o varios con su parte; el resto, el fideicomiso.
  const opcionesQuien = opcionesQuienPuso(fiduciantes.data ?? [], grupos.data ?? []);
  const cambiarPusieron = (cambio: (lineas: LineaPuso[]) => LineaPuso[]) => setFormItem((f) => ({ ...f, pusieron: cambio(f.pusieron) }));
  const elegirLoPago = (valor: string) => cambiarPusieron(() => (valor === PAGO_FIDEICOMISO ? [] : [{ quien: valor, monto: "" }]));
  const entreVarios = () => cambiarPusieron((l) => (l.length === 0 ? [{ quien: "", monto: "" }, { quien: "", monto: "" }] : [...l, { quien: "", monto: "" }]));
  const cambiarPuso = (i: number, campo: keyof LineaPuso, valor: string) => cambiarPusieron((l) => l.map((x, j) => (j !== i ? x : campo === "quien" ? { ...x, quien: valor } : { ...x, monto: valor })));
  // Si queda uno solo, vuelve a ser "Lo pagó" por todo el pago.
  const quitarPuso = (i: number) => cambiarPusieron((l) => {
    const resto = l.filter((_, j) => j !== i);
    const [solo] = resto;
    if (resto.length !== 1 || !solo) return resto;
    return solo.quien ? [{ quien: solo.quien, monto: "" }] : [];
  });

  // De qué cajas salió (Mati, 2026-10-24: parte de una y parte de otra): la elegida pasa a ser la primera línea.
  const cambiarPartesCaja = (cambio: (f: FormItem) => ParteCaja[]) => setFormItem((f) => ({ ...f, partesCaja: cambio(f) }));
  const variasCajas = () => cambiarPartesCaja((f) => (f.partesCaja.length === 0 ? [{ caja_id: f.caja_id, monto: "" }, { caja_id: "", monto: "" }] : [...f.partesCaja, { caja_id: "", monto: "" }]));
  const cambiarParteCaja = (i: number, campo: keyof ParteCaja, valor: string) => cambiarPartesCaja((f) => f.partesCaja.map((p, j) => (j === i ? { ...p, [campo]: valor } : p)));
  // Si queda una sola, vuelve a ser "Caja" por todo el monto.
  const quitarParteCaja = (i: number) => setFormItem((f) => {
    const resto = f.partesCaja.filter((_, j) => j !== i);
    const [sola] = resto;
    return resto.length === 1 && sola ? { ...f, caja_id: sola.caja_id, partesCaja: [] } : { ...f, partesCaja: resto };
  });

  /** Guarda el pago, gasto o previsto; devuelve a dónde subir el archivo y su nombre, o null si falló. */
  const guardarSegunTipo = async (tipo: TipoItemCompra): Promise<{ url: string; concepto: string } | null> => {
    if (tipo === "previsto") {
      const guardado = await accionPrevisto.run({ id: editandoPrevisto?.id ?? null, cuerpo: aPayloadPrevisto(formItemActual) });
      return guardado ? { url: `${ruta}/previstos/${guardado.id}/adjuntos`, concepto: guardado.concepto } : null;
    }
    const creado = await accionItem.run(aPayloadItem(formItemActual, tipo));
    return creado ? { url: `/api/fideicomisos/${fid}/gastos/${creado.gasto_id}/adjuntos`, concepto: creado.concepto } : null;
  };

  const guardarItem = async (e: FormEvent) => {
    e.preventDefault();
    setMostrarErroresItem(true);
    if (!tipoItem || Object.keys(erroresItem).length > 0) return;
    const guardado = await guardarSegunTipo(tipoItem);
    if (!guardado) return;
    setAvisoArchivo(null);
    if (formItemActual.archivo && !(await accionArchivo.run({ url: guardado.url, archivo: formItemActual.archivo }))) {
      setAvisoArchivo(`Se guardó «${guardado.concepto}», pero el archivo no se subió. Probá de nuevo.`);
    }
    setTipoItem(null);
    setEditandoPrevisto(null);
    ficha.recargar();
  };

  // --- compra: borrar un pago o gasto (es borrar el gasto, con su pago, sus honorarios, sus archivos y, si lo pagó un
  // fiduciante, su aporte) o un previsto (con sus archivos)
  const [aBorrar, setABorrar] = useState<ItemCompra | null>(null);
  const [previstoABorrar, setPrevistoABorrar] = useState<ItemPrevisto | null>(null);
  const accionBorrarItem = useAsyncAction((gastoId: number) => api.delete<void>(`/api/fideicomisos/${fid}/gastos/${gastoId}`).then(() => true));
  const accionBorrarPrevisto = useAsyncAction((id: number) => api.delete<void>(`${ruta}/previstos/${id}`).then(() => true));

  const pedirBorrar = (item: ItemCompra) => {
    accionBorrarItem.limpiarError();
    setABorrar(item);
  };

  const confirmarBorrar = async () => {
    if (!aBorrar) return;
    if (await accionBorrarItem.run(aBorrar.gasto_id)) {
      setABorrar(null);
      ficha.recargar();
    }
  };

  const pedirBorrarPrevisto = (p: ItemPrevisto) => {
    accionBorrarPrevisto.limpiarError();
    setPrevistoABorrar(p);
  };

  const confirmarBorrarPrevisto = async () => {
    if (!previstoABorrar) return;
    if (await accionBorrarPrevisto.run(previstoABorrar.id)) {
      setPrevistoABorrar(null);
      ficha.recargar();
    }
  };

  // --- compra: confirmar (elegir o subir el scan de la escritura + casilla) y reabrir
  const [confirmarAbierto, setConfirmarAbierto] = useState(false);
  const [formConfirmar, setFormConfirmar] = useState<FormConfirmar>({ escritura_adjunto_id: null, confirmo: false });
  const [mostrarErroresConfirmar, setMostrarErroresConfirmar] = useState(false);
  const [errorArchivoEscritura, setErrorArchivoEscritura] = useState<string | null>(null);
  const accionConfirmar = useAsyncAction((escrituraId: number) => api.post<unknown>(`${ruta}/confirmar-compra`, { escritura_adjunto_id: escrituraId }).then(() => true));
  const accionSubirEscritura = useAsyncAction((archivo: File) => {
    const form = new FormData();
    form.append("archivo", archivo);
    return api.upload<Adjunto>(`${ruta}/adjuntos`, form);
  });
  const erroresConfirmar = validarConfirmar(formConfirmar);

  const abrirConfirmar = () => {
    if (!ficha.data) return;
    setFormConfirmar({ escritura_adjunto_id: adjuntoEscrituraSugerido(ficha.data.adjuntos), confirmo: false });
    setMostrarErroresConfirmar(false);
    setErrorArchivoEscritura(null);
    accionConfirmar.limpiarError();
    accionSubirEscritura.limpiarError();
    setConfirmarAbierto(true);
  };

  const subirEscritura = async (archivo: File) => {
    const invalido = validarArchivo(archivo);
    setErrorArchivoEscritura(invalido);
    if (invalido) return;
    const subido = await accionSubirEscritura.run(archivo);
    if (subido) {
      setFormConfirmar((f) => ({ ...f, escritura_adjunto_id: subido.id }));
      ficha.recargar();
    }
  };

  const confirmarCompra = async (e: FormEvent) => {
    e.preventDefault();
    setMostrarErroresConfirmar(true);
    if (Object.keys(erroresConfirmar).length > 0 || formConfirmar.escritura_adjunto_id === null) return;
    if (await accionConfirmar.run(formConfirmar.escritura_adjunto_id)) {
      setConfirmarAbierto(false);
      ficha.recargar();
    }
  };

  const [reabrirAbierto, setReabrirAbierto] = useState(false);
  const accionReabrir = useAsyncAction(() => api.post<unknown>(`${ruta}/reabrir-compra`).then(() => true));

  const pedirReabrir = () => {
    accionReabrir.limpiarError();
    setReabrirAbierto(true);
  };

  const reabrirCompra = async () => {
    if (await accionReabrir.run()) {
      setReabrirAbierto(false);
      ficha.recargar();
    }
  };

  // --- adjuntos
  const accionSubir = useAsyncAction((archivo: File) => {
    const form = new FormData();
    form.append("archivo", archivo);
    return api.upload<Adjunto>(`${ruta}/adjuntos`, form);
  });
  const accionBorrarAdjunto = useAsyncAction((adjuntoId: number) => api.delete<void>(`/api/fideicomisos/${fid}/adjuntos/${adjuntoId}`).then(() => true));

  const subir = async (archivo: File) => {
    accionBorrarAdjunto.limpiarError();
    if (await accionSubir.run(archivo)) ficha.recargar();
  };

  const borrarAdjunto = async (a: Adjunto) => {
    accionSubir.limpiarError();
    if (await accionBorrarAdjunto.run(a.id)) ficha.recargar();
  };

  return {
    esAdmin,
    terreno: ficha.data ?? null,
    cargando: !ficha.data && ficha.loading,
    errorCarga: ficha.data ? null : (ficha.error?.message ?? null),
    reintentar: ficha.recargar,
    // ubicación
    ubicacionAbierta,
    abrirUbicacion,
    cerrarUbicacion: () => setUbicacionAbierta(false),
    form,
    cambiar,
    errores: intentado ? erroresTerreno : SIN_ERRORES_TERRENO,
    guardar,
    guardando: accionGuardar.loading,
    errorGuardar: accionGuardar.error,
    mostrarBuscador: Boolean(ficha.data?.autocompletado) && !buscadorFallo,
    avisoBuscador: buscadorFallo ? "No se pudo buscar la dirección en Google Maps. Escribila a mano." : null,
    sinBuscador: Boolean(ficha.data) && !ficha.data?.autocompletado,
    buscarDirecciones,
    elegirDireccion,
    fallarBuscador: () => setBuscadorFallo(true),
    cargandoDetalle: accionDetalle.loading,
    // compra: precio
    precioAbierto,
    abrirPrecio,
    cerrarPrecio: () => setPrecioAbierto(false),
    formPrecio: formPrecioActual,
    cambiarPrecio: (campo: keyof FormPrecio, valor: string) => setFormPrecio((f) => ({ ...f, [campo]: valor })),
    erroresPrecio: mostrarErroresPrecio ? erroresPrecio : SIN_ERRORES_PRECIO,
    guardarPrecio,
    guardandoPrecio: accionPrecio.loading,
    errorPrecio: accionPrecio.error,
    // compra: pagos, gastos y previstos
    tipoItem,
    editandoPrevisto,
    abrirItem,
    editarPrevisto,
    cerrarItem: () => setTipoItem(null),
    hayQuienPuso: opcionesQuien.length > 0,
    opcionesLoPago: [{ valor: PAGO_FIDEICOMISO, etiqueta: "El fideicomiso" }, ...opcionesQuien],
    loPago: formItemActual.pusieron[0]?.quien || PAGO_FIDEICOMISO,
    elegirLoPago,
    entreVarios,
    /** Las opciones de la línea i: sin los que ya están en otra línea. */
    opcionesPuso: (i: number) => opcionesQuien.filter((o) => !formItemActual.pusieron.some((l, j) => j !== i && l.quien === o.valor)),
    cambiarPuso,
    quitarPuso,
    restoDelFideicomiso: restoDelFideicomiso(formItemActual),
    formItem: formItemActual,
    // De qué caja sale el pago o el gasto (Mati, 2026-10-19); un previsto no mueve plata. Con una sola caja activa no se pregunta.
    opcionesCaja: tipoItem !== "previsto" && hayVariasCajas(cajas.data ?? []) ? opcionesCaja(cajas.data ?? []) : null,
    variasCajas,
    /** Las opciones de la línea i: sin las que ya están en otra línea. */
    opcionesParteCaja: (i: number) => opcionesCaja(cajas.data ?? []).filter((o) => !formItemActual.partesCaja.some((p, j) => j !== i && p.caja_id === o.valor)),
    cambiarParteCaja,
    quitarParteCaja,
    /** Lo que falta o sobra repartir entre las cajas, a la vista mientras se completa. */
    avisoCajas: erroresItem.cajas ?? null,
    cambiarItem,
    erroresItem: mostrarErroresItem ? erroresItem : SIN_ERRORES_ITEM,
    feePrevisto: feePct && tipoItem !== "pago" && formItemActual.lleva_fee ? feePrevisto(formItemActual.monto, feePct) : null,
    feePorcentaje: feePct,
    guardarItem,
    guardandoItem: accionItem.loading || accionPrevisto.loading || accionArchivo.loading,
    errorItem: tipoItem === "previsto" ? accionPrevisto.error : accionItem.error,
    avisoArchivo,
    aBorrar,
    pedirBorrar,
    cancelarBorrar: () => setABorrar(null),
    confirmarBorrar,
    borrando: accionBorrarItem.loading,
    errorBorrar: accionBorrarItem.error,
    previstoABorrar,
    pedirBorrarPrevisto,
    cancelarBorrarPrevisto: () => setPrevistoABorrar(null),
    confirmarBorrarPrevisto,
    borrandoPrevisto: accionBorrarPrevisto.loading,
    errorBorrarPrevisto: accionBorrarPrevisto.error,
    // compra: confirmar y reabrir
    confirmarAbierto,
    abrirConfirmar,
    cerrarConfirmar: () => setConfirmarAbierto(false),
    formConfirmar,
    elegirEscritura: (id: number) => setFormConfirmar((f) => ({ ...f, escritura_adjunto_id: id })),
    marcarConfirmo: (confirmo: boolean) => setFormConfirmar((f) => ({ ...f, confirmo })),
    erroresConfirmar: mostrarErroresConfirmar ? erroresConfirmar : SIN_ERRORES_CONFIRMAR,
    subirEscritura,
    subiendoEscritura: accionSubirEscritura.loading,
    errorEscritura: errorArchivoEscritura ?? accionSubirEscritura.error,
    confirmarCompra,
    confirmando: accionConfirmar.loading,
    errorConfirmar: accionConfirmar.error,
    reabrirAbierto,
    pedirReabrir,
    cancelarReabrir: () => setReabrirAbierto(false),
    reabrirCompra,
    reabriendo: accionReabrir.loading,
    errorReabrir: accionReabrir.error,
    // adjuntos
    rutaDescarga: (a: Adjunto) => `/api/fideicomisos/${fid}/adjuntos/${a.id}/descarga`,
    subir,
    borrarAdjunto,
    subiendo: accionSubir.loading,
    errorAdjuntos: accionSubir.error ?? accionBorrarAdjunto.error,
  };
}
